// Activity Logs API layer for HMS-INTERNAL-BACKEND
import http from './http';

function handleError(error) {
  const resData = error?.response?.data;
  if (resData) {
    const msg = resData.message || error?.message || 'Request failed';
    // If detailed field errors exist (Zod), include them
    const details = Array.isArray(resData.errors)
      ? resData.errors.map(e => e.field ? `${e.field}: ${e.message}` : e.message).join('; ')
      : (resData.error || '');
    const full = details ? `${msg} — ${details}` : msg;
    throw new Error(full);
  }
  throw new Error(error?.message || 'Request failed');
}

// GET /api/v1/activity-logs with query params
export async function getActivityLogs({ 
  page = 1, 
  limit = 10, 
  userId = '', 
  userEmail = '',
  userRole = '',
  activityType = '', 
  action = '', 
  resource = '', 
  success = '', 
  dateFrom = '',
  dateTo = '',
  startDate = '', 
  endDate = '',
  search = '',
  sortBy = 'timestamp',
  sortOrder = 'desc'
} = {}) {
  try {
    // Filter out empty string parameters to avoid validation errors
    const params = { page, limit };
    if (userId && userId.trim() !== '') params.userId = userId;
    if (userEmail && userEmail.trim() !== '') params.userEmail = userEmail;
    if (userRole && userRole.trim() !== '') params.userRole = userRole;
    if (activityType && activityType.trim() !== '') params.activityType = activityType;
    if (action && action.trim() !== '') params.action = action;
    if (resource && resource.trim() !== '') params.resource = resource;
    if (success && success.trim() !== '') params.success = success;
    // Support both dateFrom/dateTo and startDate/endDate naming
    if (dateFrom && dateFrom.trim() !== '') params.dateFrom = dateFrom;
    if (dateTo && dateTo.trim() !== '') params.dateTo = dateTo;
    if (startDate && startDate.trim() !== '') params.dateFrom = startDate;
    if (endDate && endDate.trim() !== '') params.dateTo = endDate;
    if (search && search.trim() !== '') params.search = search;
    if (sortBy && sortBy.trim() !== '') params.sortBy = sortBy;
    if (sortOrder && sortOrder.trim() !== '') params.sortOrder = sortOrder;
    
    const res = await http.get('/api/v1/activity-logs', { params });
    return { data: res.data };
  } catch (e) { handleError(e); }
}

// GET /api/v1/activity-logs/stats
export async function getActivityLogStats() {
  try {
    const res = await http.get('/api/v1/activity-logs/stats');
    return { data: res.data };
  } catch (e) { handleError(e); }
}

// GET /api/v1/activity-logs/filter-options
export async function getActivityLogFilterOptions() {
  try {
    const res = await http.get('/api/v1/activity-logs/filter-options');
    return { data: res.data };
  } catch (e) { handleError(e); }
}

// GET /api/v1/activity-logs/export
export async function exportActivityLogs({ 
  format = 'json', 
  userId = '', 
  userEmail = '',
  userRole = '',
  activityType = '', 
  action = '', 
  resource = '', 
  success = '', 
  dateFrom = '',
  dateTo = '',
  search = ''
} = {}) {
  try {
    const params = { format };
    if (userId && userId.trim() !== '') params.userId = userId;
    if (userEmail && userEmail.trim() !== '') params.userEmail = userEmail;
    if (userRole && userRole.trim() !== '') params.userRole = userRole;
    if (activityType && activityType.trim() !== '') params.activityType = activityType;
    if (action && action.trim() !== '') params.action = action;
    if (resource && resource.trim() !== '') params.resource = resource;
    if (success && success.trim() !== '') params.success = success;
    if (dateFrom && dateFrom.trim() !== '') params.dateFrom = dateFrom;
    if (dateTo && dateTo.trim() !== '') params.dateTo = dateTo;
    if (search && search.trim() !== '') params.search = search;
    
    const res = await http.get('/api/v1/activity-logs/export', { params });
    return { data: res.data, headers: res.headers };
  } catch (e) { handleError(e); }
}

// GET /api/v1/activity-logs/users/:userId
export async function getUserActivityLogs(userId, { page = 1, limit = 10, startDate = '', endDate = '' } = {}) {
  try {
    const params = { page, limit };
    if (startDate && startDate.trim() !== '') params.startDate = startDate;
    if (endDate && endDate.trim() !== '') params.endDate = endDate;
    
    const res = await http.get(`/api/v1/activity-logs/users/${userId}`, { params });
    return { data: res.data };
  } catch (e) { handleError(e); }
}

// GET /api/v1/activity-logs/:id
export async function getActivityLogById(logId) {
  try {
    const res = await http.get(`/api/v1/activity-logs/${logId}`);
    return { data: res.data };
  } catch (e) { handleError(e); }
}

/**
 * DELETE /api/v1/activity-logs/cleanup
 * Two modes:
 *   - Pass { daysToKeep: N }         → deletes all logs older than N days
 *   - Pass { dateFrom, dateTo }       → deletes logs within that date range
 */
export async function cleanActivityLogs({ daysToKeep, dateFrom, dateTo } = {}) {
  try {
    const body = {};
    if (dateFrom && dateTo) {
      body.dateFrom = dateFrom;
      body.dateTo   = dateTo;
    } else if (daysToKeep) {
      body.daysToKeep = daysToKeep;
    }
    const res = await http.delete('/api/v1/activity-logs/cleanup', { data: body });
    return { data: res.data };
  } catch (e) { handleError(e); }
}